import React, { useEffect, useMemo, useState } from 'react'

import { clearFilters } from 'slices/filters'

import { Div, H4, Tag } from '@driftt/tide-core'

import { AccountActivityHeader } from 'components/AccountActivityHeader'
import FeedbackModal from 'components/FeedbackModal'
import InlineNotificationsWarning from 'components/InlineNotificationsWarning'
import SavedViewsSidebar from 'components/SavedViewsSidebar'
import SaveFilterModal from 'components/SaveFilterModal'
import { driftLoadingStateBuilder } from 'components/States'
import SubNavigation from 'components/SubNavigation'
import Toast from 'components/Toast'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import useSavedFilterEditModal from 'hooks/useSavedFilterEditModal'
import useSavedFilters, { INACTIVE_FILTER } from 'hooks/useSavedFilters'
import { TrafficControlAccess } from 'layouts/AccountActivity'
import AccountsPage from 'pages/AccountsPage'
import { FilterPage } from 'utils/filters'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

interface SavedViewsPageProps {
  access: TrafficControlAccess
}

const SavedViews = ({ access }: SavedViewsPageProps) => {
  const [toastText, setToastText] = useState<string | undefined>()
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)
  const { loaded: filtersLoaded } = useAppSelector(state => state.filters)
  const LoadingComponent = useMemo(() => driftLoadingStateBuilder('Loading your accounts'), [])

  const {
    activeSavedFilter,
    defaultFilterId,
    savedFilters,
    updateActiveFilterId,
    updateDefaultFilterId,
    createNewSavedFilter,
    updateSavedFilter,
    removeSavedFilter
  } = useSavedFilters(setToastText)

  const {
    showSavedFilterModal,
    editableFilterGroups,
    editableSavedFilter,
    onOpenNewModal,
    onOpenEditModal,
    onCloseModal
  } = useSavedFilterEditModal()

  useEffect(() => {
    if (toastText) {
      const timeout = setTimeout(() => setToastText(undefined), 5000)
      return () => clearTimeout(timeout)
    }
  }, [toastText])

  const dispatch = useAppDispatch()
  const onClearFilterGroups = (page: FilterPage) => dispatch(clearFilters({ page }))

  const onClearFilters = () => {
    updateActiveFilterId(INACTIVE_FILTER)
    onClearFilterGroups(FilterPage.ACTIVITIES)
    onClearFilterGroups(FilterPage.ACCOUNTS)
  }

  useEffect(() => {
    segmentTrack(TrackType.ACCOUNT_PAGE_ACCESSED)
  }, [])

  if (!filtersLoaded) {
    return <LoadingComponent />
  }

  return (
    <Div className={styles.container}>
      {showSavedFilterModal && (
        <SaveFilterModal
          editableGroups={editableFilterGroups}
          savedFilter={editableSavedFilter}
          defaultFilterId={defaultFilterId}
          onClose={onCloseModal}
          createSavedFilter={createNewSavedFilter}
          updateSavedFilter={updateSavedFilter}
          withDefaultFilter={true}
        />
      )}
      {showFeedbackModal && <FeedbackModal onClose={() => setShowFeedbackModal(false)} />}
      {toastText && <Toast text={toastText}></Toast>}
      <Div className={styles.AccountsPageContainer}>
        <SubNavigation />
        <Div className={styles.GroupedActivitiesPage}>
          <AccountActivityHeader title="Account engagement" />
          <Div className={styles.ActivitiesContainer}>
            <Div className={styles.ActivitiesLeftContainer}>
              <SavedViewsSidebar
                savedFilters={savedFilters}
                activeFilterId={activeSavedFilter?.id || INACTIVE_FILTER}
                defaultFilterId={defaultFilterId}
                setActiveFilterId={updateActiveFilterId}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                onOpenEditModal={onOpenEditModal}
                onCreateSavedFilter={onOpenNewModal}
                onShowFeedbackModal={() => setShowFeedbackModal(true)}
                isExternalPage={true}
              />
            </Div>
            <Div className={styles.ActivitiesRightContainer}>
              <InlineNotificationsWarning savedFilters={savedFilters} />
              <Div className={styles.ActivitiesCard}>
                <Div className={styles.DescriptionRow}>
                  <Div className={styles.ActivityViewName}>
                    <H4>{activeSavedFilter?.name || 'All accounts'}</H4>
                    {!!defaultFilterId && activeSavedFilter?.id === defaultFilterId && (
                      <Tag backgroundColor="#C7E1FF" textColor="#082544" text="Default" />
                    )}
                  </Div>
                </Div>

                <AccountsPage
                  access={access}
                  onClear={onClearFilters}
                  onOpenEditModal={onOpenEditModal}
                  activeFilterId={activeSavedFilter?.id || INACTIVE_FILTER}
                  onFilterChange={() => updateActiveFilterId(INACTIVE_FILTER)}
                />
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
    </Div>
  )
}

export default SavedViews
